/**
 * FERP Portal - Entity Tabulator Table
 *
 * Generic Tabulator wrapper that derives columns and search fields
 * from SubgraphEntityType metadata. Replaces the payroll-specific
 * TabulatorTable with a universal, data-driven grid.
 *
 * Features:
 * - Columns from entity metadata (identifierFields + displayField)
 * - Search across ALL visible string columns (not hardcoded fields)
 * - Pagination (50/100/200), sorting, column reorder
 * - Export (CSV, JSON, Excel)
 * - Bulk selection
 * - Life-critical safety badge column
 *
 * FERP Principles:
 * - Data-driven: Columns from SubgraphEntityType, not hardcoded
 * - Dumb frontend: No business logic, just rendering
 */

import { Tabulator, type ColumnDefinition } from '@/types/tabulator';
import 'tabulator-tables/dist/css/tabulator.min.css';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Group, ActionIcon, Menu, Stack, TextInput, Text } from '@mantine/core';
import { IconDownload, IconSearch } from '@tabler/icons-react';
import type { SubgraphEntityType } from '@/hooks/useTenantSubgraphConfig';
import '@/styles/tabulator-mantine.css';
import { autoColumnsFromData } from '@ferp/workflow-forms';

export interface EntityTabulatorTableProps {
  entityType: SubgraphEntityType;
  data: Record<string, unknown>[];
  /** DRR-103: Field names hidden by lens evaluation (Phase 3 setting rules) */
  hiddenFields?: string[];
  onRowClick?: (row: Record<string, unknown>) => void;
  onSelectRows?: (rows: Record<string, unknown>[]) => void;
  exportFileName?: string;
  pageSize?: number;
  enableSearch?: boolean;
  enableBulkActions?: boolean;
}

/** Convert camelCase/snake_case field name to human-readable title */
function humanize(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

/** Build Tabulator columns from SubgraphEntityType metadata (Tier 1) */
export function buildColumnsFromMetadata(entityType: SubgraphEntityType): ColumnDefinition[] {
  const columns: ColumnDefinition[] = [];
  const addedFields = new Set<string>();

  // 1. ID column (first identifier field, frozen)
  const idField = entityType.identifierFields[0] ?? 'id';
  columns.push({
    title: 'ID', field: idField, sorter: 'string',
    width: 140, frozen: true, headerFilter: 'input' as unknown as boolean,
  });
  addedFields.add(idField);

  // 2. Display field (the human-readable name)
  if (entityType.displayField && !addedFields.has(entityType.displayField)) {
    columns.push({
      title: humanize(entityType.displayField),
      field: entityType.displayField, sorter: 'string',
      headerFilter: 'input' as unknown as boolean,
    });
    addedFields.add(entityType.displayField);
  }

  // 3. Additional identifier fields
  for (const field of entityType.identifierFields.slice(1)) {
    if (addedFields.has(field)) continue;
    columns.push({
      title: humanize(field), field, sorter: 'string',
      headerFilter: 'input' as unknown as boolean,
    });
    addedFields.add(field);
  }

  // 4. Status column (FERP entities always have status)
  columns.push({
    title: 'Status', field: 'status', sorter: 'string', width: 120,
    headerFilter: 'list' as unknown as boolean,
    headerFilterParams: { values: { '': 'All', ACTIVE: 'Active', DRAFT: 'Draft', SUSPENDED: 'Suspended', ARCHIVED: 'Archived' } },
  });

  // 5. Safety level column for life-critical entities
  if (entityType.isLifeCritical) {
    columns.push({
      title: 'Safety', field: 'safetyLevel', width: 110, sorter: 'string',
    });
  }

  // 6. Updated timestamp
  columns.push({
    title: 'Updated', field: 'updatedAt', sorter: 'string', width: 160,
  });

  return columns;
}

/** Extract searchable field names from columns */
function getSearchFields(columns: ColumnDefinition[]): string[] {
  return columns
    .filter((c) => c.field && c.field !== 'status' && c.field !== 'safetyLevel')
    .map((c) => c.field as string);
}

export function EntityTabulatorTable({
  entityType,
  data,
  hiddenFields,
  onRowClick,
  onSelectRows,
  exportFileName,
  pageSize = 50,
  enableSearch = true,
  enableBulkActions = false,
}: EntityTabulatorTableProps): React.ReactElement {
  const tableRef = useRef<HTMLDivElement>(null);
  const tableInstanceRef = useRef<any | null>(null);
  const tableBuiltRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCount, setSelectedCount] = useState(0);

  const metadataColumns = buildColumnsFromMetadata(entityType);
  const metadataFieldSet = new Set(metadataColumns.map((c) => c.field).filter(Boolean) as string[]);
  const allColumns = [...metadataColumns, ...autoColumnsFromData(data, metadataFieldSet)];
  // DRR-103 Phase 3: Filter out columns hidden by lens evaluation
  const hiddenSet = hiddenFields?.length ? new Set(hiddenFields) : null;
  const columns = hiddenSet
    ? allColumns.filter((c) => !c.field || !hiddenSet.has(c.field))
    : allColumns;
  const searchFields = getSearchFields(columns as ColumnDefinition[]);

  const fileName = exportFileName ?? `${entityType.name.toLowerCase()}_export`;

  // Initialize Tabulator
  useEffect(() => {
    if (!tableRef.current) return;

    // @ts-expect-error Type assignment
    const enhancedColumns: ColumnDefinition[] = enableBulkActions
      ? [{ formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', frozen: true, width: 50 }, ...columns]
      : columns;

    try {
      tableBuiltRef.current = false;
      const instance = new Tabulator(tableRef.current, {
        data,
        columns: enhancedColumns,
        layout: 'fitColumns',
        pagination: true,
        paginationMode: 'local',
        paginationSize: pageSize,
        paginationSizeSelector: [50, 100, 200],
        responsiveLayout: 'collapse',
        placeholder: `No ${entityType.pluralName.toLowerCase()} found`,
        movableColumns: true,
        selectable: enableBulkActions ? true : false,
        selectableRangeMode: 'click',
        height: '600px',
      });

      tableInstanceRef.current = instance;

      instance.on('tableBuilt', () => {
        tableBuiltRef.current = true;
      });

      if (onRowClick) {
        instance.on('rowClick', (_e: any, row: any) => onRowClick(row.getData()));
      }
      if (enableBulkActions) {
        instance.on('rowSelectionChanged', () => {
          const rows = instance.getSelectedData() ?? [];
          setSelectedCount(rows.length);
          onSelectRows?.(rows);
        });
      }
    } catch (error) {
      console.error('Failed to initialize Tabulator:', error);
    }

    return () => {
      tableBuiltRef.current = false;
      tableInstanceRef.current?.destroy();
    };
    // Reinitialize when entity type, data, or hidden fields change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType.graphqlType, data, pageSize, hiddenFields]);

  // Search filter: OR across all searchable columns
  useEffect(() => {
    const instance = tableInstanceRef.current;
    if (!instance || !tableBuiltRef.current) return;

    if (searchTerm) {
      const filters = searchFields.map((field) => ({ field, type: 'like' as const, value: searchTerm }));
      instance.setFilter([filters, 'or'] as unknown as Parameters<typeof instance.setFilter>[0]);
    } else {
      instance.clearFilter();
    }
  }, [searchTerm, searchFields]);

  const handleExport = useCallback((format: 'csv' | 'json' | 'xlsx') => {
    const instance = tableInstanceRef.current;
    if (!instance) return;
    const ts = new Date().toISOString().split('T')[0];
    instance.download(format, `${fileName}_${ts}.${format}`);
  }, [fileName]);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        {enableSearch && (
          <TextInput
            placeholder={`Search ${entityType.pluralName.toLowerCase()}...`}
            leftSection={<IconSearch size={16} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            style={{ flex: 1, maxWidth: 350 }}
          />
        )}
        <Group gap="xs">
          {selectedCount > 0 && (
            <Text size="sm" c="dimmed">{selectedCount} selected</Text>
          )}
          <Menu position="bottom-end" shadow="md">
            <Menu.Target>
              <ActionIcon variant="light" aria-label="Export">
                <IconDownload size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => handleExport('csv')}>Download CSV</Menu.Item>
              <Menu.Item onClick={() => handleExport('json')}>Download JSON</Menu.Item>
              <Menu.Item onClick={() => handleExport('xlsx')}>Download Excel</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      <div ref={tableRef} style={{ width: '100%' }} />
    </Stack>
  );
}

export default EntityTabulatorTable;
